import React from 'react';

import introImg from '../../../../assets/images/courses/teamplayer&collaboration/chapter5.1.jpg';
import chapterImg1 from '../../../../assets/images/courses/teamplayer&collaboration/chapter5.2.jpg';
import chapterImg2 from '../../../../assets/images/courses/teamplayer&collaboration/chapter5.3.jpg';
import chapterImg3 from '../../../../assets/images/courses/teamplayer&collaboration/chapter5.4.jpg';
import chapterImg4 from '../../../../assets/images/courses/teamplayer&collaboration/chapter5.5.jpg';
import { CourseChapter } from '../../../../courses/courses.types';
import { MediaBlock } from '../../MediaBlock';
import { TableQAComponent } from '../../TableQAComponent';
import { TabsBlock } from '../../TabsBlock';

interface TeamPlayersAndCollaborationChapter1 {
  chapter: CourseChapter;
  onNextChapter: (nextIndex: number) => void;
  totalPages: number;
  courseId?: string;
  onQuizResult?: (result: boolean | undefined) => void;
}

export const TeamPlayersAndCollaborationChapter5: React.FC<TeamPlayersAndCollaborationChapter1> = ({
  chapter,
  onNextChapter,
  totalPages,
}) => {
  const handleNextChapterClick = () => {
    onNextChapter(chapter.index + 1);
  };
  const showYoureListeningTabData = [
    {
      title: 'Values and Beliefs: ',
      mediaImage: '',

      htmlContent: `<p>These are the things that the team thinks are really important. For example, your team might value honesty, meaning everyone is expected to tell the truth and be open about their thoughts.</p>`,
    },
    {
      title: 'Behavior and Norms: ',
      mediaImage: '',
      htmlContent: `<p>This is about how people act and what’s considered normal. If your team always starts meetings on time and everyone listens when someone is speaking, that’s part of your team’s culture.</p>`,
    },
    {
      title: 'Rituals and Traditions: ',
      mediaImage: '',
      htmlContent: `<p>These are the regular activities or traditions your team has. Maybe you have a weekly check-in or a fun tradition of sharing something positive that happened each week.</p>`,
    },
    {
      title: 'Leadership Style: ',
      mediaImage: '',
      htmlContent: `<p>The way the team leader behaves also shapes the culture. A leader who is supportive and encourages everyone to share ideas helps create a positive team culture.</p>`,
    },
  ];
  const showYoureListeningTabData2 = [
    {
      title: 'Teamwork: ',
      mediaImage: '',

      htmlContent: `<p>A good culture makes it easier for everyone to work together. When everyone respects each other and communicates well, the team can achieve more.</p>`,
    },
    {
      title: 'Motivation: ',
      mediaImage: '',
      htmlContent: `<p>When the team culture is positive, people are usually more motivated to do their best. They feel good about being part of the team and want to contribute.</p>`,
    },
    {
      title: 'Problem-Solving: ',
      mediaImage: '',
      htmlContent: `<p>In a team with a strong culture, people are better at handling problems. They can talk openly about issues and find solutions together.</p>`,
    },
    {
      title: 'Enjoyment: ',
      mediaImage: '',
      htmlContent: `<p>Simply put, a good team culture makes work more enjoyable. When people get along and support each other, it’s more fun to be part of the team.</p>`,
    },
  ];
  const handlePrint = (view: string) => {
    const printArea = document.getElementById(view);
    if (printArea) {
      printArea.classList.add('print-view');

      const cleanup = () => {
        printArea.classList.remove('print-view');
        window.onafterprint = null;
      };

      window.onafterprint = cleanup;

      window.print();
    }
  };
  const tableQAdata = [
    'Working the expected start and end times',
    'Following the dress code',
    'Working safely',
    'Completing work on schedule',
    'Showing up on time',
    'Wearing headphones',
    'Complaining',
  ];
  const tableQAAnswerKey: ('check' | 'cross' | ('check' | 'cross')[])[] = [
    'check',
    ['check', 'cross'],
    'check',
    'check',
    'check',
    ['check', 'cross'],
    'cross',
  ];

  return (
    <>
      <div className="content-wrap lg:border-l lg:pl-8 lg:pt-8 pt-4 pb-10">
        <p className="text-sm italic  mb-7">
          Part {chapter.index} of {totalPages}
        </p>
        <h2 className="font-semibold text-3xl">What is Team Culture</h2>
        <br />
        <MediaBlock mediaImage={introImg}>
          <p className="text-3xl leading-10 font-light w-3/4">
            "A strong team can take any crazy vision and turn it into reality."
            <br />— John Carmack
          </p>
        </MediaBlock>
        <p className="text-lg text-left font-semibold mt-10 mb-10">
          This quote highlights how a positive team culture can transform ambitious ideas into real
          achievements. When a team works well together, supporting each other and embracing a
          shared goal, they can accomplish amazing things. It's a reminder that the strength of a
          team lies not just in individual talents, but in how well members collaborate and lift
          each other up.
        </p>
        <br />
        <p className="text-2xl text-center font-light mt-10 mb-10">
          Imagine you're in a group project at school. How your group works together, communicates,
          and handles challenges is a lot like what we call "team culture" in the workplace.
        </p>
        <div className="md:flex items-center mt-10 mb-10">
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg1} alt="" />
          </div>
          <p className="flex-1 leading-7">
            Team culture is the vibe, or the overall feeling and behavior, that everyone in the team
            contributes to. It’s about how people treat each other, work together, and get things
            done. Let’s break it down a bit more.
          </p>
        </div>
        <br />
        <h3 className="text-2xl text-purple-600 font-bold mt-10 mb-1">What is team culture?</h3>
        <br />
        <p className="text-2xl text-center font-light mt-10 mb-10">
          Team culture is like the personality of a group, defining how members interact, work
          together, and handle challenges. It’s built on shared values, behaviors, and traditions
          that shape the overall vibe of the team.
        </p>
        <br />
        <div className="md:flex items-center mt-10 mb-10">
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg2} alt="" />
          </div>
          <p className="flex-1 leading-7">
            Whether you're working on a school project or playing on a sports team, understanding
            and contributing to a positive team culture can make a huge difference. It helps
            everyone feel respected, motivated, and ready to tackle problems together, making the
            experience more enjoyable and productive for everyone involved.
          </p>
        </div>
        <h3 className="text-2xl text-purple-600 font-bold mb-5">
          Clothing and Teamwork … is there a connection?
        </h3>
        <div className="md:flex items-center mt-10 mb-10">
          <p className="flex-1 leading-7">
            According to research, an important function of a company dress code is its ability to
            unite employees and foster a cohesive team. We don’t usually think about them as a
            ‘dress code’ but sports uniforms, how musicians dress in a band, and what certain groups
            of people choose to wear are all types of dress codes.
          </p>
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg3} alt="" />
          </div>
        </div>
        <br />
        <p className="text-2xl text-center font-light mt-10 mb-10">
          Let's look at some key components of team culture. Click continue below.
        </p>
        <br />
        <h3 className="text-2xl text-purple-600 font-bold mb-5">
          {' '}
          Key Components of Team Culture{' '}
        </h3>
        <br />
        <p className="text-2xl text-center font-light mt-10 mb-10">
          Team culture is shaped by several key components that guide how members interact, make
          decisions, and work together.
        </p>
        <TabsBlock tabsData={showYoureListeningTabData} />
        <br />
        <h3 className="text-2xl text-purple-600 font-bold mb-5">Why Team Culture is Important</h3>
        <p className="text-lg text-left font-light mt-10 mb-10">
          A positive team culture significantly enhances various aspects of teamwork and
          productivity. <strong>Click each tab to learn more.</strong>
        </p>
        <br />
        <TabsBlock tabsData={showYoureListeningTabData2} />
        <br />
        <h3 className="text-2xl text-purple-600 font-bold mb-5">
          Activity: Workplace Culture - Fitting In:{' '}
        </h3>
        <p className="text-lg text-left font-light mt-10 mb-10">
          Not all workplaces have a culture that is a good fit with our skills or our values. It is
          useful to think about what attitudes, behaviors and characteristics are commonly sought
          after by employers and what our values and attributes are, so we can determine if we are a
          match before we take a job or join a team.
        </p>
        <br />
        <div className="md:flex items-center mt-10 mb-10">
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg4} alt="" />
          </div>
          <p className="flex-1 leading-7">
            <br />
            Not all workplaces have a culture that is a good fit with our skills or our values. It
            is useful to think about what attitudes, behaviors and characteristics are commonly
            sought after by employers and what our values and attributes are, so we can determine if
            we are a match before we take a job or join a team.
          </p>
        </div>
        Key Components
        <br />
        {/* Horizontal Line   ..................................................................................*/}
        <div className="relative flex items-center justify-center w-full mb-8 mt-8">
          <div className="w-full border-t border-gray-300"></div>
          <div
            className="absolute bg-purple-500 text-white font-bold flex items-center justify-center 
      w-10 h-10 rounded-full text-lg shadow-lg"
          >
            1
          </div>
        </div>
        <br />
        <h3 className="text-2xl  font-bold mt-10 mb-5">Part1:</h3>
        <p className="text-lg text-left font-light mt-5 mb-10">
          Below is a list of attitudes, behaviors and characteristics that contribute to workplace
          culture. Click on the check mark for those you think are valued in all workplaces. Click
          the X beside those that you think might only be valued in certain workplaces or not valued
          at all.
        </p>
        {/* table pending */}
        <TableQAComponent data={tableQAdata} answerKey={tableQAAnswerKey} />
        <br />
        <p className="text-xl text-left font-bold mt-10 mb-10">
          Fitting into the culture at a workplace or on a team, in a positive way, helps us to build
          trust with our co-workers and supports our participation on teams or when working with a
          partner.
        </p>
        <div className="relative flex items-center justify-center w-full mb-8 mt-8">
          <div className="w-full border-t border-gray-300"></div>
          <div
            className="absolute bg-purple-500 text-white font-bold flex items-center justify-center 
      w-10 h-10 rounded-full text-lg shadow-lg"
          >
            2
          </div>
        </div>
        <br />
        <div id="print-view1" className="mt-10 mb-10">
          <ol className=" pl-5 mb-14 text-xl font-light">
            <li>
              <p className="text-xl font-light flex-1 mt-5 mb-5">
                <strong> Part 2:</strong> Make brief notes regarding why you made each of the
                choices you made.
              </p>

              <textarea
                className="w-full  h-32 p-2 border rounded"
                placeholder="Enter your text here..."
              ></textarea>
            </li>
          </ol>

          <div className="mt-5 text-right">
            <button onClick={() => handlePrint('print-view1')} className="btn-internal">
              PRINT
            </button>
          </div>
        </div>
        <br />
        <h3 className="text-2xl mt-2 text-center font-light mb-5">
          Next we'll discuss suggestions on how you can become a team culture creator.
        </h3>
        <button onClick={handleNextChapterClick} className="btn-internal w-full">
          Becoming a Team Culture Creator
        </button>
      </div>
    </>
  );
};
